import { AxiosError } from 'axios';
import { getConfig } from 'config';
import { loginErrorFormatter, loginTaskStatusFormatter } from 'formatters';
import { createContext, useContext, useCallback, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { updateAxiosHeader } from 'utils/axios';

import API from 'api';
import { LoginErrorModel, LOGIN_TASK_STATUS, UserLoginViewModel } from 'api/utility/utility.model';
import Loading from 'components/Icons/Loading';
import { COMPANY } from 'models/company.model';
import { CurrentUser } from 'models/user.model';

import { useCurrentUserQuery } from './useCurrentUserQuery';
import { useFullIdentityLoginMutation } from './useFullIdentityLoginMutation';
import { useFullIdentityLoginTaskQuery } from './useFullIdentityLoginTaskQuery';
import { useLoginMutation } from './useLoginMutation';

export interface AuthenticatedContextProps {
  currentUser: CurrentUser;
  loginError: string | null;
  isLoading: boolean;
  handleLogin: (loginData: UserLoginViewModel, company: COMPANY) => void;
  handleFullIdentityLogin: (loginData: UserLoginViewModel, company: COMPANY) => void;
  handleLogout: () => void;
  clearLoginError: () => void;
}

const defaultValues: AuthenticatedContextProps = {
  currentUser: null,
  loginError: null,
  isLoading: false,
  handleLogin: () => {},
  handleFullIdentityLogin: () => {},
  handleLogout: () => {},
  clearLoginError: () => {},
};

const AuthenticatedContext = createContext(defaultValues);

export const useAuthenticatedContext = () => useContext(AuthenticatedContext);

interface AuthenticatedContextProviderProps {
  children: ReactNode;
}

export const AuthenticatedContextProvider = ({ children }: AuthenticatedContextProviderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const token = localStorage.getItem('JWTToken');
  const company = localStorage.getItem('company') as COMPANY | undefined;

  const [taskId, setTaskId] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  const currentUserQueryEnabled = useMemo(() => !!(token && company), [company, token]);

  const {
    data: currentUser,
    isLoading: isLoadingUser,
    refetch,
  } = useCurrentUserQuery({
    retry: 2,
    enabled: currentUserQueryEnabled,
    onError: async (error) => {
      if (error.response?.status === 403) {
        try {
          const { token } = await API.utility.getRefreshToken();

          localStorage.setItem('JWTToken', token);
          refetch();
        } catch (error) {
          handleLogout();
        }
      }
      handleLogout();
    },
  });

  const onError = useCallback(
    ({ response }: AxiosError<LoginErrorModel>) => {
      const errorStatus = response?.data?.statusCode;
      setLoginError(loginErrorFormatter(t, errorStatus));
    },
    [t]
  );

  const { mutate: login, isLoading: isLoginLoading } = useLoginMutation({
    onSuccess: ({ token }) => {
      localStorage.setItem('JWTToken', token);
      updateAxiosHeader();
    },
    onError,
  });

  const { mutate: fullIdentityLogin, isLoading: isFullIdentityLoginLoading } =
    useFullIdentityLoginMutation({
      onSuccess: ({ taskId }) => setTaskId(taskId),
      onError,
    });

  useFullIdentityLoginTaskQuery(taskId as string, {
    useErrorBoundary: true,
    enabled: !!taskId,
    refetchInterval: (response) =>
      response?.status === LOGIN_TASK_STATUS.IN_PROGRESS ? 3000 : false,
    onSuccess: ({ status, token }) => {
      if (status === LOGIN_TASK_STATUS.SUCCESS && !!token) {
        localStorage.setItem('JWTToken', token);

        updateAxiosHeader();
        return;
      }

      if (status !== LOGIN_TASK_STATUS.IN_PROGRESS) {
        setTaskId(null);
        setLoginError(loginTaskStatusFormatter(t, status));
      }
    },
    onError,
  });

  const handleLogin = useCallback(
    (data: UserLoginViewModel, company: COMPANY) => {
      login(data);
    },
    [login]
  );

  const handleFullIdentityLogin = useCallback(
    (data: UserLoginViewModel, company: COMPANY) => {
      fullIdentityLogin(data);
    },
    [fullIdentityLogin]
  );

  const handleLogout = useCallback(() => {
    navigate(0);
    localStorage.removeItem('JWTToken');
    localStorage.removeItem('company');
    localStorage.removeItem('certificateTab');
    updateAxiosHeader();
    queryClient.clear();
  }, [navigate, queryClient]);

  const contextValue = useMemo(() => {
    let user: CurrentUser = null;

    if (company && currentUser) {
      user = {
        ...getConfig(company),
        ...currentUser,
      };
    }

    return {
      currentUser: user,
      loginError,
      isLoading: isLoadingUser || isLoginLoading || isFullIdentityLoginLoading || !!taskId,
      handleLogin,
      handleFullIdentityLogin,
      handleLogout,
      clearLoginError: () => {
        if (loginError) setLoginError(null);
      },
    };
  }, [
    company,
    currentUser,
    loginError,
    isLoadingUser,
    isLoginLoading,
    isFullIdentityLoginLoading,
    taskId,
    handleLogin,
    handleFullIdentityLogin,
    handleLogout,
  ]);

  return (
    <AuthenticatedContext.Provider value={contextValue}>
      {isLoadingUser ? <Loading isFullScreen={true} /> : children}
    </AuthenticatedContext.Provider>
  );
};
